import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  decideRequestApplication,
  getRequestApplicationById,
  updateRequestApplication,
} from "../../api/requests";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const STATUS_COLOR = {
  Pending: "bg-amber-100 text-amber-700",
  Approved: "bg-green-100 text-green-700",
  Declined: "bg-red-100 text-red-700",
};

const normalizeFieldLabel = (value) => `${value || ""}`.trim().toLowerCase();

export default function RequestDetails() {
  const { companyAccount, currentEmployee } = useCurrentEmployee();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get("applicationId") || "";
  const cacheKey = useMemo(
    () => `cehr_request_details_${applicationId || "unknown"}`,
    [applicationId],
  );
  const cachedData = useMemo(
    () => readSessionCache(cacheKey, { application: null }),
    [cacheKey],
  );

  const [loading, setLoading] = useState(!cachedData?.application);
  const [submitting, setSubmitting] = useState(false);
  const [application, setApplication] = useState(cachedData?.application || null);
  const [decisionReason, setDecisionReason] = useState("");
  const [editing, setEditing] = useState(false);
  const [editedResponses, setEditedResponses] = useState([]);

  const approvalSteps = Array.isArray(application?.approvalSnapshot) ? application.approvalSnapshot : [];
  const currentStep = approvalSteps[application?.currentApprovalStep || 0];
  const employeePositionId = currentEmployee?.positionId || currentEmployee?.position?.id;
  const canDecide = useMemo(
    () => application?.status === "Pending" && (companyAccount || (
      !!employeePositionId
      && currentStep?.positionId === employeePositionId
      && (currentStep?.approvers || []).some((approver) => approver.employeeId === currentEmployee?.id)
    )),
    [application, companyAccount, currentEmployee?.id, currentStep, employeePositionId],
  );
  const canEdit = application?.status === "Pending" && (application?.currentApprovalStep || 0) === 0 && !(application?.approvalHistory || []).length && (companyAccount || application?.employee?.id === currentEmployee?.id);

  const requestFields = application?.requestType?.fields || [];
  const fieldForResponse = (response) => requestFields.find((field) =>
    field.id === response?.fieldId
    || normalizeFieldLabel(field.label) === normalizeFieldLabel(response?.label),
  );

  const startEditing = () => {
    setEditedResponses((application.responses || []).map((response) => {
      const field = fieldForResponse(response);
      return {
        ...response,
        fieldId: field?.id || response?.fieldId,
        label: field?.label || response?.label,
      };
    }));
    setEditing(true);
  };

  const saveEdits = async () => {
    const missingField = requestFields.find((field) => {
      if (!field.isRequired) return false;
      const response = editedResponses.find((item) =>
        item.fieldId === field.id
        || normalizeFieldLabel(item.label) === normalizeFieldLabel(field.label),
      );
      return !`${response?.value ?? ""}`.trim();
    });
    if (missingField) {
      toast.error(`${missingField.label} is required.`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await updateRequestApplication(applicationId, { responses: editedResponses });
      setApplication(response?.application || application);
      setEditing(false);
      toast.success("Request updated successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update request.");
    } finally {
      setSubmitting(false);
    }
  };

  const loadDetails = useCallback(async ({ silent = false } = {}) => {
    if (!applicationId) {
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      const response = await getRequestApplicationById(applicationId);
      const nextApplication = response?.application || null;
      setApplication(nextApplication);
      writeSessionCache(cacheKey, { application: nextApplication });
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to load request details.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [applicationId, cacheKey]);

  useEffect(() => {
    loadDetails({ silent: true });
  }, [applicationId, loadDetails]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadDetails({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadDetails]);

  const handleDecision = async (status) => {
    if (!canDecide) {
      return;
    }

    if (!decisionReason.trim()) {
      toast.error("Decision reason is required.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await decideRequestApplication(applicationId, {
        status,
        reason: decisionReason.trim(),
        adminOverride: companyAccount || undefined,
      });

      setApplication(response?.application || null);
      writeSessionCache(cacheKey, { application: response?.application || null });
      setDecisionReason("");
      toast.success(`Request ${status.toLowerCase()} successfully.`);
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to update request status.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!applicationId) {
    return (
      <div className="p-6 font-Outfit">
        <div className="bg-white rounded-xl border border-[#EEF0F6] p-6">
          Missing request application ID.
        </div>
      </div>
    );
  }

  if (loading) {
    return <FetchLoadingState message="Loading request details..." className="min-h-[60vh] font-Outfit" />;
  }

  if (!application) {
    return (
      <div className="p-6 font-Outfit">
        <div className="bg-white rounded-xl border border-[#EEF0F6] p-6">Request application not found.</div>
      </div>
    );
  }

  return (
    <div className="p-6 font-Outfit min-h-screen">
      <h2 className="text-2xl font-semibold text-[#071C50] mb-5">Request Details</h2>

      <div className="bg-white rounded-xl border border-[#EEF0F6] shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-sm text-[#667085]">Request Type</p>
            <p className="text-xl font-semibold text-[#071C50]">{application.requestType?.name || "-"}</p>
            <p className="text-sm text-[#667085] mt-1">
              Submitted on {new Date(application.submittedAt).toLocaleString()}
            </p>
          </div>

          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              STATUS_COLOR[application.status] || ""
            }`}
          >
            {application.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          <div>
            <p className="text-sm text-[#667085]">Applicant Name</p>
            <p className="font-medium text-[#071C50]">{application.applicantName}</p>
          </div>
          <div>
            <p className="text-sm text-[#667085]">Applicant Email</p>
            <p className="font-medium text-[#071C50]">{application.applicantEmail}</p>
          </div>
          <div>
            <p className="text-sm text-[#667085]">Applicant Phone</p>
            <p className="font-medium text-[#071C50]">{application.applicantPhone || "-"}</p>
          </div>
          <div>
            <p className="text-sm text-[#667085]">Employee ID</p>
            <p className="font-medium text-[#071C50]">{application.employee?.employeeNumber || "-"}</p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-base font-semibold text-[#071C50] mb-3">Approval Flow</h3>
          <div className="space-y-2">
            {approvalSteps.map((step, index) => {
              const decision = (application.approvalHistory || []).find((item) => item.order === index + 1);
              const state = decision?.status || (application.status === "Declined" && index > application.currentApprovalStep ? "Stopped" : index === application.currentApprovalStep && application.status === "Pending" ? "Awaiting approval" : "Waiting");
              return <div key={`${step.positionId}-${index}`} className="flex items-center gap-3 rounded-lg border border-[#EAECF0] p-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EEF2FF] text-sm font-bold text-[#1234CD]">{index + 1}</span><div className="flex-1"><p className="font-medium text-[#071C50]">{step.positionName}</p><p className="text-xs text-[#667085]">{state}{decision?.decidedByName ? ` by ${decision.decidedByName}` : ""}</p></div></div>;
            })}
            {approvalSteps.length === 0 ? <p className="text-sm text-[#98A2B3]">No approval flow was captured for this request.</p> : null}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-base font-semibold text-[#071C50] mb-3">Submitted Responses</h3>
          <div className="space-y-3">
            {(editing ? editedResponses : application.responses || []).map((response, index) => {
              const field = fieldForResponse(response);
              const updateValue = (value) => setEditedResponses((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, value } : item));
              return <div
                key={`${response?.fieldId || "field"}-${index}`}
                className="border border-[#EAECF0] rounded-lg p-4"
              >
                <p className="text-sm text-[#667085]">{response?.label || "Question"}</p>
                {editing ? field?.fieldType === "long_text" ? <textarea value={`${response?.value || ""}`} onChange={(event) => updateValue(event.target.value)} className="mt-2 min-h-20 w-full rounded-md border border-[#D0D5DD] px-3 py-2 outline-none" /> : field?.fieldType === "single_select" ? <select value={`${response?.value || ""}`} onChange={(event) => updateValue(event.target.value)} className="mt-2 w-full rounded-md border border-[#D0D5DD] px-3 py-2 outline-none"><option value="">Select option</option>{(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}</select> : <input type={field?.fieldType === "date" ? "date" : field?.fieldType === "number" ? "number" : "text"} value={`${response?.value || ""}`} onChange={(event) => updateValue(event.target.value)} className="mt-2 w-full rounded-md border border-[#D0D5DD] px-3 py-2 outline-none" /> : <p className="text-[#101828] mt-1 whitespace-pre-wrap">{`${response?.value || "-"}`}</p>}
              </div>
            })}

            {(!application.responses || application.responses.length === 0) && (
              <p className="text-sm text-[#98A2B3]">No response data submitted.</p>
            )}
            {canEdit ? <div className="flex gap-2 pt-2">{editing ? <><button type="button" onClick={saveEdits} disabled={submitting} className="rounded-md bg-[#1234CD] px-4 py-2 text-sm text-white">Save changes</button><button type="button" onClick={() => setEditing(false)} disabled={submitting} className="rounded-md border border-[#D0D5DD] px-4 py-2 text-sm">Cancel</button></> : <button type="button" onClick={startEditing} className="rounded-md border border-[#1234CD] px-4 py-2 text-sm text-[#1234CD]">Edit request</button>}</div> : null}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-base font-semibold text-[#071C50] mb-3">Request Timeline</h3>
          <div className="space-y-3 border border-[#EAECF0] rounded-lg p-4 text-sm">
            <div><p className="font-medium text-[#071C50]">Request submitted</p><p className="text-xs text-[#667085]">{new Date(application.submittedAt).toLocaleString()}</p></div>
            {(application.editHistory || []).map((item, index) => <div key={`edit-${index}`} className="border-t border-[#EAECF0] pt-3"><p className="font-medium text-[#071C50]">Request edited by {item.editedByName || "Employee"}</p><p className="text-xs text-[#667085]">{item.editedAt ? new Date(item.editedAt).toLocaleString() : "-"}</p></div>)}
            {(application.approvalHistory || []).map((item, index) => <div key={`${item.order}-${index}`} className="border-t border-[#EAECF0] pt-3"><p className="font-medium text-[#071C50]">{item.status} by {item.decidedByName || item.positionName}{item.adminOverride ? " (administrator override)" : ""}</p><p className="text-xs text-[#667085]">{item.positionName} · {item.decidedAt ? new Date(item.decidedAt).toLocaleString() : "-"}</p><p className="mt-1 whitespace-pre-wrap text-[#475467]">{item.reason || "No reason supplied"}</p></div>)}
            {(application.approvalHistory || []).length === 0 ? <p className="text-[#667085]">No approval decision has been made yet.</p> : null}
            {application.decidedAt ? <div className="border-t border-[#EAECF0] pt-3"><p className="font-medium text-[#071C50]">Final outcome recorded</p><p className="text-xs text-[#667085]">{new Date(application.decidedAt).toLocaleString()} · {application.decisionBy || "Unknown actor"}</p>{application.decisionReason ? <p className="mt-1 whitespace-pre-wrap text-[#475467]">{application.decisionReason}</p> : null}</div> : null}
          </div>
        </div>

        {companyAccount || canDecide ? (
          <div className="border-t border-[#EAECF0] pt-5">
            <h3 className="text-base font-semibold text-[#071C50] mb-1">{companyAccount ? "Administrator override" : currentStep ? `${currentStep.positionName} Decision` : "Decision"}</h3>
            {companyAccount ? <p className="mb-3 text-sm text-amber-700">This bypasses the configured employee for the current step and will be recorded in the audit history.</p> : null}
            <textarea
              value={decisionReason}
              onChange={(event) => setDecisionReason(event.target.value)}
              disabled={!canDecide}
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none min-h-[100px] disabled:bg-gray-100"
              placeholder="Provide decision reason..."
            />

            <div className="flex flex-wrap gap-2 mt-3">
              <button
                className="px-4 py-2 rounded-md bg-green-600 text-white disabled:opacity-60"
                onClick={() => handleDecision("Approved")}
                disabled={!canDecide || submitting}
              >
                Accept Request
              </button>
              <button
                className="px-4 py-2 rounded-md bg-red-600 text-white disabled:opacity-60"
                onClick={() => handleDecision("Declined")}
                disabled={!canDecide || submitting}
              >
                Decline Request
              </button>
              {!canDecide && (
                <p className="text-sm text-[#667085] self-center">
                  This request has already been decided.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
